import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../api/api';

const ManagerViewPage = () => {
  const navigate = useNavigate();
  const [marketBasedData, setMarketBasedData] = useState([]);
  const [hotelExpenseData, setHotelExpenseData] = useState([]);
  const [allMainGroups, setAllMainGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtreler
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedBudgetType, setSelectedBudgetType] = useState('ALL');
  const [isMarketBased] = useState(true); // Varsayılan olarak Pazar Bazlı görünüm

  useEffect(() => {
    fetchMarketBasedData();
    fetchHotelExpenseData();
    fetchAllMainGroups();
  }, []);

  const fetchMarketBasedData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiRequest('/marketing-budget/manager-view/market_based_data/');
      setMarketBasedData(result || []);
    } catch (err) {
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHotelExpenseData = async () => {
    try {
      const result = await apiRequest('/marketing-budget/market-hotel-analysis/');
      setHotelExpenseData(result || []);
    } catch (err) {
      // Silently handle error
    }
  };

  const fetchAllMainGroups = async () => {
    try {
      const result = await apiRequest('/marketing-budget/manager-view/all_main_groups/');
      setAllMainGroups(result || []);
    } catch (err) {
      // Silently handle error
    }
  };

  const exportToExcel = () => {
    // TODO: Implement Excel export
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0 €';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Pazar bazlı veri organizasyonu
  const organizeMarketBasedData = (dataToOrganize = marketBasedData) => {
    if (!dataToOrganize || dataToOrganize.length === 0) return { groups: {}, years: [], budgetTypes: [] };

    // Market bazlı olmayan gruplar - bu gruplar için market ayrımı yapılmaz
    // NOT: "AKKA Bonus" artık market bazlı veri gösteriyor (marketing_budget_percentage tablosunda market_name var)
    const NON_MARKET_BASED_GROUPS = ['Temsil Ağırlama', 'Web Yazılım', 'Dijital Pazarlama'];

    // Sabit market listesi - tüm pazarları içerir
    const ALL_MARKETS = [
      'Avrupa',
      'Balkan',
      'Baltık',
      'BDT',
      'İç Pazar',
      'İncentive'
    ];

    const groups = {};
    const years = new Set();
    const budgetTypes = new Set();

    // Tüm ana grupları al (verisi olsun olmasın)
    const allMainGroupsList = allMainGroups.length > 0 ? allMainGroups : [...new Set(dataToOrganize.map(item => item.maingroup_name))].sort();

    // Önce tüm ana grupları oluştur (verisi olsun olmasın)
    allMainGroupsList.forEach(maingroup_name => {
      groups[maingroup_name] = {
        name: maingroup_name,
        markets: {},
        isNonMarketBased: NON_MARKET_BASED_GROUPS.includes(maingroup_name)
      };

      // Her ana grup için tüm marketleri oluştur (boş olsa bile)
      ALL_MARKETS.forEach(market_name => {
        groups[maingroup_name].markets[market_name] = {
          name: market_name,
          subgroups: {},
          yearlyTotals: {}
        };
      });
    });

    dataToOrganize.forEach(item => {
      const { maingroup_name, market_name, subgroup_name, budget_year, budgettype_name, budget_amount } = item;

      years.add(budget_year);
      budgetTypes.add(budgettype_name);

      // TÜM BÜTÇE KALEMLERİ İÇİN AYNI MANTIK:
      // market_name varsa ve geçerli bir market ise → O market'e atansın
      // market_name yoksa veya geçersizse → Avrupa'ya atansın (fallback)
      let targetMarket;
      if (market_name && ALL_MARKETS.includes(market_name)) {
        targetMarket = market_name; // Veritabanındaki market_name'i kullan
      } else {
        targetMarket = ALL_MARKETS[0]; // Market yoksa veya geçersizse Avrupa'ya atansın
      }

      if (!groups[maingroup_name] || !groups[maingroup_name].markets[targetMarket]) {
        return;
      }

      if (!groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name]) {
        groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name] = {
          name: subgroup_name,
          yearlyAmounts: {}
        };
      }

      if (!groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name].yearlyAmounts[budget_year]) {
        groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name].yearlyAmounts[budget_year] = {};
      }

      if (!groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name].yearlyAmounts[budget_year][budgettype_name]) {
        groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name].yearlyAmounts[budget_year][budgettype_name] = 0;
      }
      groups[maingroup_name].markets[targetMarket].subgroups[subgroup_name].yearlyAmounts[budget_year][budgettype_name] += parseFloat(budget_amount || 0);

      if (!groups[maingroup_name].markets[targetMarket].yearlyTotals[budget_year]) {
        groups[maingroup_name].markets[targetMarket].yearlyTotals[budget_year] = {};
      }

      if (!groups[maingroup_name].markets[targetMarket].yearlyTotals[budget_year][budgettype_name]) {
        groups[maingroup_name].markets[targetMarket].yearlyTotals[budget_year][budgettype_name] = 0;
      }

      groups[maingroup_name].markets[targetMarket].yearlyTotals[budget_year][budgettype_name] += parseFloat(budget_amount || 0);
    });

    // Tüm kombinasyonları oluştur (boş olsa bile)
    const allYears = Array.from(years).sort();
    const allBudgetTypes = Array.from(budgetTypes).sort();

    Object.values(groups).forEach(mainGroup => {
      // Sadece sabit listedeki marketleri işle
      ALL_MARKETS.forEach(marketName => {
        const market = mainGroup.markets[marketName];
        if (!market) return;

        Object.values(market.subgroups).forEach(subgroup => {
          // Her subgroup için tüm yıl x bütçe tipi kombinasyonlarını oluştur
          allYears.forEach(year => {
            if (!subgroup.yearlyAmounts[year]) {
              subgroup.yearlyAmounts[year] = {};
            }
            allBudgetTypes.forEach(budgetType => {
              if (subgroup.yearlyAmounts[year][budgetType] === undefined) {
                subgroup.yearlyAmounts[year][budgetType] = 0;
              }
            });
          });
        });

        // Market toplamları için de aynı şeyi yap
        allYears.forEach(year => {
          if (!market.yearlyTotals[year]) {
            market.yearlyTotals[year] = {};
          }
          allBudgetTypes.forEach(budgetType => {
            if (market.yearlyTotals[year][budgetType] === undefined) {
              market.yearlyTotals[year][budgetType] = 0;
            }
          });
        });
      });
    });

    return {
      groups,
      years: allYears,
      budgetTypes: allBudgetTypes
    };
  };

  const getMarketGroupTotal = (mainGroup, market, year, budgetType) => {
    const { groups } = organizeMarketBasedData(filteredMarketData);

    if (!groups[mainGroup] || !groups[mainGroup].markets[market] || !groups[mainGroup].markets[market].yearlyTotals[year]) {
      return 0;
    }

    const yearlyTotals = groups[mainGroup].markets[market].yearlyTotals[year];

    if (yearlyTotals[budgetType] !== undefined) {
      return yearlyTotals[budgetType] || 0;
    }

    const normalizedBudgetType = (budgetType || '').toUpperCase().trim();
    const matchedKey = Object.keys(yearlyTotals).find(bt => {
      const normalizedBt = (bt || '').toUpperCase().trim();
      return normalizedBt === normalizedBudgetType;
    });

    if (matchedKey) {
      return yearlyTotals[matchedKey] || 0;
    }

    if (yearlyTotals[''] !== undefined) {
      return yearlyTotals[''] || 0;
    }

    return 0;
  };

  // Ana grup bazlı toplam bütçe hesaplama (tüm marketler için toplam)
  const getMainGroupTotal = (mainGroup, year, budgetType) => {
    const { groups } = organizeMarketBasedData(filteredMarketData);

    if (!groups[mainGroup]) {
      return 0;
    }

    let total = 0;

    // Tüm marketler için toplam bütçeyi topla
    Object.keys(groups[mainGroup].markets).forEach(market => {
      const marketData = groups[mainGroup].markets[market];
      if (marketData.yearlyTotals[year]) {
        const yearlyTotals = marketData.yearlyTotals[year];

        // Direkt eşleşme varsa ekle
        if (yearlyTotals[budgetType] !== undefined) {
          total += yearlyTotals[budgetType] || 0;
          return;
        }

        // Case-insensitive eşleşme dene
        const normalizedBudgetType = (budgetType || '').toUpperCase().trim();
        const matchedKey = Object.keys(yearlyTotals).find(bt => {
          const normalizedBt = (bt || '').toUpperCase().trim();
          return normalizedBt === normalizedBudgetType;
        });

        if (matchedKey) {
          total += yearlyTotals[matchedKey] || 0;
        } else if (yearlyTotals[''] !== undefined) {
          total += yearlyTotals[''] || 0;
        }
      }
    });

    return total;
  };

  const getMarketSubGroupAmount = (mainGroup, market, subGroup, year, budgetType) => {
    const { groups } = organizeMarketBasedData(filteredMarketData);
    if (!groups[mainGroup] || !groups[mainGroup].markets[market] || !groups[mainGroup].markets[market].subgroups[subGroup]) return 0;
    if (!groups[mainGroup].markets[market].subgroups[subGroup].yearlyAmounts[year]) return 0;
    return groups[mainGroup].markets[market].subgroups[subGroup].yearlyAmounts[year][budgetType] || 0;
  };

  // Otel dağılım verilerini organize et (HAM VERİ FORMATI - marketing_budget_percentage tablosundan)
  const organizeHotelExpenseData = (dataToOrganize = hotelExpenseData) => {
    if (!dataToOrganize || dataToOrganize.length === 0) {
      return { groups: {}, years: [], budgetTypes: [], markets: [] };
    }

    // Market bazlı olmayan gruplar - bu gruplar için market ayrımı yapılmaz
    // NOT: "AKKA Bonus" artık market bazlı veri gösteriyor (marketing_budget_percentage tablosunda market_name var)
    const NON_MARKET_BASED_GROUPS = ['Temsil Ağırlama', 'Web Yazılım', 'Dijital Pazarlama'];

    // Sabit market listesi - sol tabloyla aynı olmalı
    const ALL_MARKETS = [
      'Avrupa',
      'Balkan',
      'Baltık',
      'BDT',
      'İç Pazar',
      'İncentive'
    ];

    const groups = {};
    const years = new Set();
    const budgetTypes = new Set();

    // Tüm ana grupları al (verisi olsun olmasın)
    const allMainGroupsList = allMainGroups.length > 0 ? allMainGroups : [...new Set(dataToOrganize.map(item => item.maingroup_name))].sort();

    // Önce tüm ana grupları oluştur (verisi olsun olmasın)
    allMainGroupsList.forEach(maingroup_name => {
      groups[maingroup_name] = {
        name: maingroup_name,
        markets: {}
      };

      // Her ana grup için tüm marketleri oluştur (boş olsa bile)
      ALL_MARKETS.forEach(market_name => {
        groups[maingroup_name].markets[market_name] = {
          name: market_name,
          yearlyData: {} // year -> budgetType -> { hotel_name -> percentage } şeklinde
        };
      });
    });

    // Ham veriyi organize et (her satır bir hotel_name için)
    dataToOrganize.forEach(item => {
      const { maingroup_name, market_name, budget_year, budget_type, budgettype_name, hotel_name, percentage } = item;

      // budgettype_name veya budget_type kullan (uyumluluk için)
      const budgetTypeKey = budgettype_name || budget_type || '';

      if (budget_year) years.add(budget_year);
      if (budgetTypeKey) budgetTypes.add(budgetTypeKey);

      // TÜM BÜTÇE KALEMLERİ İÇİN AYNI MANTIK:
      // market_name varsa ve geçerli bir market ise → O market'e atansın
      // market_name yoksa veya geçersizse → Avrupa'ya atansın (fallback)
      let targetMarket;
      if (market_name && ALL_MARKETS.includes(market_name)) {
        targetMarket = market_name; // Veritabanındaki market_name'i kullan
      } else {
        targetMarket = ALL_MARKETS[0]; // Market yoksa veya geçersizse Avrupa'ya atansın
      }

      if (!groups[maingroup_name] || !groups[maingroup_name].markets[targetMarket]) {
        return;
      }

      // Year altında budgetType oluştur
      if (!groups[maingroup_name].markets[targetMarket].yearlyData[budget_year]) {
        groups[maingroup_name].markets[targetMarket].yearlyData[budget_year] = {};
      }
      if (!groups[maingroup_name].markets[targetMarket].yearlyData[budget_year][budgetTypeKey]) {
        groups[maingroup_name].markets[targetMarket].yearlyData[budget_year][budgetTypeKey] = {};
      }

      // Hotel bazında yüzdeyi sakla
      if (hotel_name) {
        groups[maingroup_name].markets[targetMarket].yearlyData[budget_year][budgetTypeKey][hotel_name.toLowerCase()] = parseFloat(percentage || 0);
      }
    });

    return {
      groups,
      years: Array.from(years).sort(),
      budgetTypes: Array.from(budgetTypes).sort(),
      markets: ALL_MARKETS // Sabit listeyi döndür
    };
  };

  const getHotelExpenseAmount = (mainGroup, market, year, budgetType, hotel) => {
    const { groups } = organizeHotelExpenseData();

    // Ana grup ve market kontrolü
    if (!groups[mainGroup] || !groups[mainGroup].markets[market]) {
      return { percentage: 0, amount: 0 };
    }

    const marketData = groups[mainGroup].markets[market];

    // Year kontrolü
    if (!marketData.yearlyData[year]) {
      return { percentage: 0, amount: 0 };
    }

    const yearData = marketData.yearlyData[year];
    const availableBudgetTypes = Object.keys(yearData);

    // Eğer budgetType "ALL" ise, mevcut olan ilk budget type'ı kullan
    let targetBudgetType = budgetType;
    if (budgetType === 'ALL' && availableBudgetTypes.length > 0) {
      targetBudgetType = availableBudgetTypes[0];
    } else if (!yearData[budgetType]) {
      // Case-insensitive eşleşme dene
      const normalizedBudgetType = (budgetType || '').toUpperCase().trim();
      targetBudgetType = availableBudgetTypes.find(bt => {
        const normalizedBt = (bt || '').toUpperCase().trim();
        return normalizedBt === normalizedBudgetType;
      });

      // Hala bulunamadıysa, boş string olarak dene
      if (!targetBudgetType && availableBudgetTypes.includes('')) {
        targetBudgetType = '';
      }

      if (!targetBudgetType) {
        return { percentage: 0, amount: 0 };
      }
    }

    const hotelData = yearData[targetBudgetType];
    if (!hotelData) {
      return { percentage: 0, amount: 0 };
    }

    // Ham veri formatı: hotelData bir obje, içinde hotel_name -> percentage mapping'i var
    // Örnek: { "antedon": 70.0, "alinda": 30.0 }
    const hotelName = hotel.toLowerCase();
    const percentage = parseFloat(hotelData[hotelName] || 0);

    // Amount hesaplamak için toplam bütçeyi bulalım
    // Önce market bazlı deneyelim, eğer 0 dönerse ana grup bazlı toplam bütçeyi kullanalım
    let totalBudget = getMarketGroupTotal(mainGroup, market, year, targetBudgetType);

    // Eğer market bazlı toplam bütçe 0 ise, ana grup bazlı toplam bütçeyi kullan
    if (totalBudget === 0) {
      totalBudget = getMainGroupTotal(mainGroup, year, targetBudgetType);
    }

    const amount = (percentage / 100) * totalBudget;

    return { percentage, amount };
  };

  // Otel dağılım tablosu render fonksiyonu (YENİ VIEW ile)
  const renderHotelExpenseTable = (year, isGlobal = false, budgetType = null) => {
    const { groups: hotelGroups, markets: orderedMarkets, budgetTypes: hotelBudgetTypes } = organizeHotelExpenseData();
    const tableTitle = isGlobal ? 'GLOBAL BAZLI' : 'MARKET BAZLI';
    const hotels = ['ANTEDON', 'ALINDA', 'CLAROS', 'LUSH', 'SUITES'];

    // Bütçe tipi: Parametre olarak gelirse onu kullan, yoksa selectedBudgetType'ı kullan
    // Ama önce hotelExpenseData'dan gelen budgetTypes ile eşleştir
    let targetBudgetType = budgetType || selectedBudgetType;

    // Eğer targetBudgetType hotelBudgetTypes içinde yoksa, case-insensitive eşleşme dene
    if (targetBudgetType && !hotelBudgetTypes.includes(targetBudgetType)) {
      const normalizedTarget = (targetBudgetType || '').toUpperCase().trim();
      const matched = hotelBudgetTypes.find(bt => {
        const normalizedBt = (bt || '').toUpperCase().trim();
        return normalizedBt === normalizedTarget;
      });
      if (matched) {
        targetBudgetType = matched;
      }
    }

    // Sol tablodaki grupları al (hizalama için)
    const { groups: marketGroups } = organizeMarketBasedData(filteredMarketData);
    const mainGroupOrder = Object.keys(marketGroups);

    // Her otel için toplam yüzdeyi hesapla (tüm ana gruplar ve marketler için)
    // Yüzdeyi direkt toplamak yerine, tutarları toplayıp toplam bütçeye göre hesapla
    const getTotalPercentageForHotel = (hotel) => {
      let totalAmount = 0;
      const groupBudgets = new Map(); // Her ana grup için toplam bütçeyi bir kez saklamak için

      mainGroupOrder.forEach(mainGroupName => {
        orderedMarkets.forEach(market => {
          const expense = getHotelExpenseAmount(mainGroupName, market, year, targetBudgetType, hotel.toLowerCase());
          totalAmount += expense.amount;

          // Her ana grup için toplam bütçeyi bir kez al (her market için aynı olacak)
          if (!groupBudgets.has(mainGroupName)) {
            const { groups } = organizeHotelExpenseData();
            if (groups[mainGroupName] && groups[mainGroupName].markets[market]) {
              const marketData = groups[mainGroupName].markets[market];
              if (marketData.yearlyData[year]) {
                const yearData = marketData.yearlyData[year];
                const availableBudgetTypes = Object.keys(yearData);
                let targetBudgetTypeKey = targetBudgetType;
                if (targetBudgetType === 'ALL' && availableBudgetTypes.length > 0) {
                  targetBudgetTypeKey = availableBudgetTypes[0];
                } else if (!yearData[targetBudgetType]) {
                  const normalizedBudgetType = targetBudgetType.toUpperCase();
                  targetBudgetTypeKey = availableBudgetTypes.find(bt => bt.toUpperCase() === normalizedBudgetType);
                }
                if (yearData[targetBudgetTypeKey]) {
                  const item = yearData[targetBudgetTypeKey];
                  const maingroupTotal = parseFloat(item.maingroup_global_total_budget || 0);
                  groupBudgets.set(mainGroupName, maingroupTotal);
                }
              }
            }
          }
        });
      });

      // Tüm ana grupların toplam bütçelerini topla
      let totalBudget = 0;
      groupBudgets.forEach(budget => {
        totalBudget += budget;
      });

      // Toplam bütçe 0 ise 0 döndür
      if (totalBudget === 0) return 0;

      // Toplam tutar / toplam bütçe * 100
      return (totalAmount / totalBudget) * 100;
    };

    return (
      <div className="mb-6">
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm" style={{ minWidth: '600px' }}>
          <thead>
            {/* İlk satır: Otel isimleri - Yeşil arka plan */}
            <tr className="bg-green-600 dark:bg-green-800">
              {hotels.map(hotel => (
                <th key={hotel} colSpan={2} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-white text-xs" style={{ minWidth: '100px' }}>
                  {hotel}
                </th>
              ))}
            </tr>
            {/* İkinci satır: Toplam yüzdeler - Yeşil arka plan */}
            <tr className="bg-green-600 dark:bg-green-800">
              {hotels.map(hotel => {
                const totalPercentage = getTotalPercentageForHotel(hotel);
                return (
                  <th key={hotel} colSpan={2} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-white text-xs" style={{ minWidth: '100px' }}>
                    {totalPercentage.toFixed(2)}%
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Object.keys(hotelGroups).length === 0 ? (
              <tr>
                <td colSpan={10} className="border border-gray-300 dark:border-gray-600 px-1 py-2 text-center text-gray-500 dark:text-gray-400 text-xs">
                  Veri yükleniyor... (Debug: {hotelExpenseData.length} kayıt bulundu)
                </td>
              </tr>
            ) : (
              // Sol tabloyla aynı sıralamada göster
              mainGroupOrder.map(mainGroupName => {
                const mainGroupData = hotelGroups[mainGroupName];
                if (!mainGroupData) return null;

                return (
                  <React.Fragment key={mainGroupName}>
                    {/* Main Group Row - Sarı renkli */}
                    <tr className="bg-yellow-200 dark:bg-yellow-800">
                      {hotels.map(hotel => {
                        // Ana grup için tüm marketlerdeki yüzde ve tutarları topla
                        const totalPercentage = orderedMarkets.reduce((sum, market) => {
                          const expense = getHotelExpenseAmount(mainGroupName, market, year, targetBudgetType, hotel.toLowerCase());
                          return sum + expense.percentage;
                        }, 0);
                        const totalAmount = orderedMarkets.reduce((sum, market) => {
                          const expense = getHotelExpenseAmount(mainGroupName, market, year, targetBudgetType, hotel.toLowerCase());
                          return sum + expense.amount;
                        }, 0);
                        return (
                          <React.Fragment key={hotel}>
                            <td className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-bold text-gray-900 dark:text-white text-xs" style={{ minWidth: '50px' }}>
                              {totalPercentage.toFixed(1)}%
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-bold text-gray-900 dark:text-white text-xs" style={{ minWidth: '50px' }}>
                              {formatCurrency(totalAmount)}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>

                    {/* Market Rows - Sol tabloyla aynı sıralama */}
                    {orderedMarkets.map(marketName => {
                      const marketData = mainGroupData.markets[marketName];
                      if (!marketData) return null; // Market yoksa atla

                      return (
                        <tr key={marketName} className="bg-gray-50 dark:bg-gray-800">
                          {hotels.map(hotel => {
                            // Market bazında doğrudan percentage ve amount al
                            const expense = getHotelExpenseAmount(mainGroupName, marketName, year, targetBudgetType, hotel.toLowerCase());
                            return (
                              <React.Fragment key={hotel}>
                                <td className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-gray-700 dark:text-gray-300 text-xs" style={{ minWidth: '50px' }}>
                                  {expense.percentage.toFixed(1)}%
                                </td>
                                <td className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-gray-700 dark:text-gray-300 text-xs" style={{ minWidth: '50px' }}>
                                  {formatCurrency(expense.amount)}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto"></div>
          <p className="text-gray-900 dark:text-white mt-4">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button
            onClick={fetchMarketBasedData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  // Filtrelenmiş veriler
  const filteredMarketData = marketBasedData.filter(item => {
    const yearMatch = selectedYear === 'ALL' || item.budget_year === selectedYear;
    const typeMatch = selectedBudgetType === 'ALL' || item.budgettype_name === selectedBudgetType;
    return yearMatch && typeMatch;
  });

  const { groups, years, budgetTypes } = organizeMarketBasedData(filteredMarketData);

  // Mevcut yıllar ve bütçe tipleri
  const availableYears = [...new Set(marketBasedData.map(item => item.budget_year))].sort();
  const availableBudgetTypes = [...new Set(marketBasedData.map(item => item.budgettype_name))].sort();

  // Görseldeki yapı için: Tüm yılların "Gerçekleşen" + Son yılın "Planlama"
  const getColumnStructure = () => {
    // Bütçe tipi isimlerini normalize et
    const getRealizedType = () => {
      return budgetTypes.find(bt =>
        bt.toUpperCase() === 'GERÇEKLEŞEN' ||
        bt.toUpperCase() === 'GERCEKLESEN' ||
        bt === 'Gerçekleşen' ||
        bt === 'Gerceklesen'
      ) || 'GERÇEKLEŞEN';
    };

    const getPlanningType = () => {
      return budgetTypes.find(bt =>
        bt.toUpperCase() === 'PLANLAMA' ||
        bt === 'Planlama'
      ) || 'PLANLAMA';
    };

    const realizedType = getRealizedType();
    const planningType = getPlanningType();
    const hasPlanning = budgetTypes.some(bt =>
      bt.toUpperCase() === 'PLANLAMA' || bt === 'Planlama'
    );

    if (selectedYear !== 'ALL') {
      // Tek yıl seçiliyse, sadece o yılın Gerçekleşen ve Planlama'sını göster
      const columns = [];
      // Gerçekleşen
      columns.push({ year: selectedYear, budgetType: realizedType });
      // Planlama (varsa)
      if (hasPlanning) {
        columns.push({ year: selectedYear, budgetType: planningType });
      }
      return columns;
    } else {
      // Tüm yıllar: Her yılın Hem Gerçekleşen Hem de Planlama'sı (varsa)
      const columns = [];
      const sortedYears = [...years].sort();

      sortedYears.forEach(year => {
        // Gerçekleşen
        columns.push({ year, budgetType: realizedType });

        // Planlama (varsa) - Artık her yıl için gösteriyoruz ki arada kalan yılların planlaması kaybolmasın
        if (hasPlanning) {
          columns.push({ year, budgetType: planningType });
        }
      });

      return columns;
    }
  };

  const columnStructure = getColumnStructure();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/marketing-budget/reports')}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Geri Dön
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Manager View - Pazar Bazlı Bütçe Raporu
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ana gruplar, pazarlar ve alt gruplar bazında yıllık bütçe analizi
                </p>
              </div>
            </div>
            <button
              onClick={exportToExcel}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Excel Export
            </button>
          </div>

          {/* Filtreler */}
          <div className="py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Yıl Seçin
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="ALL">Tümü</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bütçe Tipi
                </label>
                <select
                  value={selectedBudgetType}
                  onChange={(e) => setSelectedBudgetType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="ALL">Tümü</option>
                  {availableBudgetTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Ana Tablo ve Otel Dağılım Tablosu Yan Yana */}
        <div className="overflow-x-auto">
          <div className="flex gap-4 items-start" style={{
            minWidth: `${120 + (columnStructure.length * 80) + 700}px`
          }}>
            {/* Sol Tablo - Ana Bütçe */}
            <div className="flex-shrink-0" style={{
              minWidth: `${120 + (columnStructure.length * 80)}px`
            }}>
              <div className="text-center mb-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  BÜTÇE KATEGORİLERİ
                </h3>
              </div>
              <div className="relative">
                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm" style={{
                  minWidth: `${120 + (columnStructure.length * 80)}px`
                }}>
                  <thead>
                    {/* İlk satır: Yıllar */}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th rowSpan={2} className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-1 py-1 text-left font-semibold text-gray-900 dark:text-white text-xs" style={{
                        minWidth: selectedYear === 'ALL' ? '120px' : '100px',
                        boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                      }}>
                        Kategori Detayı
                      </th>
                      {columnStructure.map((col, index) => (
                        <th key={`year-${index}`} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-gray-900 dark:text-white text-xs" style={{ minWidth: '80px' }}>
                          {col.year}
                        </th>
                      ))}
                    </tr>
                    {/* İkinci satır: Bütçe Tipleri */}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      {columnStructure.map((col, index) => {
                        const displayType = col.budgetType.toUpperCase() === 'GERÇEKLEŞEN' || col.budgetType.toUpperCase() === 'GERCEKLESEN'
                          ? 'Gerçekleşen'
                          : col.budgetType.toUpperCase() === 'PLANLAMA'
                            ? 'Planlama'
                            : col.budgetType;
                        return (
                          <th key={`type-${index}`} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-gray-900 dark:text-white text-xs" style={{ minWidth: '80px' }}>
                            {displayType}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groups).map(([mainGroupName, mainGroupData]) => (
                      <React.Fragment key={mainGroupName}>
                        {/* Main Group Row - Sarı renkli */}
                        <tr className="bg-yellow-200 dark:bg-yellow-800">
                          <td className="sticky left-0 z-20 bg-yellow-200 dark:bg-yellow-800 border border-gray-300 dark:border-gray-600 px-1 py-1 font-bold text-gray-900 dark:text-white text-xs" style={{
                            minWidth: selectedYear === 'ALL' ? '120px' : '100px',
                            boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                          }}>
                            {mainGroupName}
                          </td>
                          {columnStructure.map((col, index) => {
                            // Ana grup toplamını hesapla (tüm pazarların toplamı)
                            const totalAmount = Object.keys(mainGroupData.markets).reduce((sum, market) => {
                              return sum + getMarketGroupTotal(mainGroupName, market, col.year, col.budgetType);
                            }, 0);
                            return (
                              <td key={`main-${index}`} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-bold text-gray-900 dark:text-white text-xs" style={{ minWidth: '80px' }}>
                                {formatCurrency(totalAmount)}
                              </td>
                            );
                          })}
                        </tr>

                        {/* Market Rows */}
                        {Object.entries(mainGroupData.markets).map(([marketName, marketData]) => (
                          <React.Fragment key={marketName}>
                            {/* Market Row */}
                            <tr className="bg-gray-50 dark:bg-gray-800">
                              <td className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-2 py-1 font-semibold text-gray-700 dark:text-gray-300 text-xs" style={{
                                minWidth: selectedYear === 'ALL' ? '120px' : '100px',
                                boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                              }}>
                                {marketName}
                              </td>
                              {columnStructure.map((col, index) => (
                                <td key={`market-${index}`} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center font-semibold text-gray-700 dark:text-gray-300 text-xs">
                                  {formatCurrency(getMarketGroupTotal(mainGroupName, marketName, col.year, col.budgetType))}
                                </td>
                              ))}
                            </tr>

                            {/* Sub Group Rows - Sadece pazar bazlı filtre kapalıyken göster */}
                            {!isMarketBased && Object.entries(marketData.subgroups).map(([subGroupName, subGroupData]) => (
                              <tr key={subGroupName} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="sticky left-0 z-20 bg-inherit border border-gray-300 dark:border-gray-600 px-3 py-1 text-gray-700 dark:text-gray-300 text-xs" style={{
                                  minWidth: selectedYear === 'ALL' ? '120px' : '100px',
                                  boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                                }}>
                                  {subGroupName}
                                </td>
                                {selectedYear === 'ALL' ? (
                                  years.map(year =>
                                    budgetTypes.map(budgetType => (
                                      <td key={`${year}-${budgetType}`} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center text-gray-700 dark:text-gray-300 text-xs">
                                        {formatCurrency(getMarketSubGroupAmount(mainGroupName, marketName, subGroupName, year, budgetType))}
                                      </td>
                                    ))
                                  )
                                ) : selectedBudgetType === 'ALL' ? (
                                  budgetTypes.map(budgetType => (
                                    <td key={budgetType} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center text-gray-700 dark:text-gray-300 text-xs">
                                      {formatCurrency(getMarketSubGroupAmount(mainGroupName, marketName, subGroupName, selectedYear, budgetType))}
                                    </td>
                                  ))
                                ) : (
                                  budgetTypes.map(budgetType => (
                                    <td key={budgetType} className="border border-gray-300 dark:border-gray-600 px-1 py-1 text-center text-gray-700 dark:text-gray-300 text-xs">
                                      {formatCurrency(getMarketSubGroupAmount(mainGroupName, marketName, subGroupName, selectedYear, budgetType))}
                                    </td>
                                  ))
                                )}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sağ Tablo - Otel Dağılımı */}
            <div className="flex-shrink-0" style={{ minWidth: '700px' }}>
              {(() => {
                // Mevcut yılları ve bütçe tiplerini al
                const sortedYears = [...availableYears].sort();
                const lastYear = sortedYears[sortedYears.length - 1];
                const previousYear = sortedYears.length > 1 ? sortedYears[sortedYears.length - 2] : null;

                // Bütçe tipi isimlerini normalize et - hotelExpenseData'dan gelen budgetTypes'ı kullan
                const { budgetTypes: hotelBudgetTypes } = organizeHotelExpenseData();
                const getRealizedType = () => {
                  // Önce hotelExpenseData'dan gelen budgetTypes'ı kontrol et
                  const fromHotel = hotelBudgetTypes.find(bt =>
                    bt && (bt.toUpperCase() === 'GERÇEKLEŞEN' ||
                      bt.toUpperCase() === 'GERCEKLESEN' ||
                      bt === 'Gerçekleşen' ||
                      bt === 'Gerceklesen')
                  );
                  if (fromHotel) return fromHotel;

                  // Yoksa availableBudgetTypes'dan ara
                  return availableBudgetTypes.find(bt =>
                    bt && (bt.toUpperCase() === 'GERÇEKLEŞEN' ||
                      bt.toUpperCase() === 'GERCEKLESEN' ||
                      bt === 'Gerçekleşen' ||
                      bt === 'Gerceklesen')
                  ) || 'GERÇEKLEŞEN';
                };

                const getPlanningType = () => {
                  // Önce hotelExpenseData'dan gelen budgetTypes'ı kontrol et
                  const fromHotel = hotelBudgetTypes.find(bt =>
                    bt && (bt.toUpperCase() === 'PLANLAMA' ||
                      bt === 'Planlama')
                  );
                  if (fromHotel) return fromHotel;

                  // Yoksa availableBudgetTypes'dan ara
                  return availableBudgetTypes.find(bt =>
                    bt && (bt.toUpperCase() === 'PLANLAMA' ||
                      bt === 'Planlama')
                  ) || 'PLANLAMA';
                };

                const realizedType = getRealizedType();
                const planningType = getPlanningType();

                // İki tablo: Son yıldan bir önceki yılın Gerçekleşen'i + Son yılın Planlama'sı
                return (
                  <div className="flex gap-6">
                    {/* İlk Tablo: Son yıldan bir önceki yılın Gerçekleşen'i */}
                    {previousYear && (
                      <div className="flex-shrink-0" style={{ minWidth: '650px' }}>
                        <div className="text-center mb-3">
                          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {previousYear} GERÇEKLEŞEN - OTEL GİDER DAĞILIMI
                          </h3>
                        </div>
                        <div className="relative">
                          {renderHotelExpenseTable(previousYear, false, realizedType)}
                        </div>
                      </div>
                    )}

                    {/* İkinci Tablo: Son yılın Planlama'sı */}
                    {lastYear && (
                      <div className="flex-shrink-0" style={{ minWidth: '650px' }}>
                        <div className="text-center mb-3">
                          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {lastYear} PLANLAMA - OTEL GİDER DAĞILIMI
                          </h3>
                        </div>
                        <div className="relative">
                          {renderHotelExpenseTable(lastYear, false, planningType)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerViewPage;